import { FieldText } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import cn from 'classnames';
import * as React from 'react';

import { durationConverter, isPositiveFiniteNumber } from 'utils/duration';

import styles from './index.module.scss';

type FieldTextProps = React.ComponentProps<typeof FieldText>;

interface Props {
  className?: string;
  disabled?: boolean;
  hoursInputProps?: Partial<FieldTextProps>;
  minutesInputProps?: Partial<FieldTextProps>;
  input: {
    onChange: (value: number) => void;
    value?: number;
  };
  meta: FieldTextProps['meta'];
  name?: string;
}

interface Duration {
  minutes: number;
  hours: number;
}

export class DurationInput extends React.PureComponent<Props> {
  private maxInputLengthForMinutes = 2;
  private maxInputLengthForHours = 4;

  getCurrentValues(seconds?: number): Duration | null {
    if (seconds === undefined) return null;
    return durationConverter.seconds.toHoursAndMinutes(seconds);
  }

  handleChange(value: Duration): void {
    const durationInSeconds =
      durationConverter.hours.toSeconds(value.hours) +
      durationConverter.minutes.toSeconds(value.minutes);

    this.props.input.onChange(durationInSeconds);
  }

  @boundMethod
  handleHoursInput(e?: React.ChangeEvent<HTMLInputElement>) {
    if (!e) return;
    const stringValue = this.stripeNonNumericChars(e.target.value);

    if (stringValue.length > this.maxInputLengthForHours) {
      return;
    }

    if (stringValue.length === 0) {
      const minutes =
        this.getCurrentValues(this.props.input.value)?.minutes || 0;
      this.handleChange({ hours: 0, minutes });
      return;
    }

    const intValue = parseInt(stringValue);

    if (isPositiveFiniteNumber(intValue)) {
      const minutes =
        this.getCurrentValues(this.props.input.value)?.minutes || 0;
      this.handleChange({ hours: intValue, minutes });
    }
  }

  @boundMethod
  handleMinutesInput(e?: React.ChangeEvent<HTMLInputElement>) {
    if (!e) return;
    const stringValue = this.stripeNonNumericChars(e.target.value);

    if (stringValue.length > this.maxInputLengthForMinutes) {
      return;
    }

    if (stringValue.length === 0) {
      const hours = this.getCurrentValues(this.props.input.value)?.hours || 0;
      this.handleChange({ hours, minutes: 0 });
      return;
    }

    const intValue = parseInt(stringValue);

    if (isPositiveFiniteNumber(intValue) && intValue < 60) {
      const hours = this.getCurrentValues(this.props.input.value)?.hours || 0;
      this.handleChange({ hours, minutes: intValue });
    }
  }

  private stripeNonNumericChars(input: string) {
    return input.replace(/[^\d]+/g, '');
  }

  render() {
    return (
      <section className={cn(styles.root, this.props.className)}>
        <FieldText
          {...this.props.hoursInputProps}
          className={styles.hoursInput}
          input={{
            name: 'hours',
            onChange: this.handleHoursInput,
            value:
              this.getCurrentValues(
                this.props.input.value,
              )?.hours?.toString() || '',
          }}
          meta={this.props.meta}
          type="text"
          disabled={this.props.disabled}
        />
        <FieldText
          {...this.props.minutesInputProps}
          className={styles.minutesInput}
          input={{
            name: 'minutes',
            onChange: this.handleMinutesInput,
            value:
              this.getCurrentValues(
                this.props.input.value,
              )?.minutes?.toString() || '',
          }}
          meta={{
            // All error messages will be showed by hours input. but to keep them in sync
            // An empty error message is sent to Minutes input to simulate having an error
            // without showing an error message.
            error: this.props.meta?.error ? ' ' : null,
            touched: this.props.meta?.touched,
          }}
          type="text"
          disabled={this.props.disabled}
        />
      </section>
    );
  }
}
